import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, MapPin, Star, ArrowUpRight, Trophy, Gift } from "lucide-react";
import api from "@/lib/api";

export default function HallOfFounders() {
  const [data, setData] = useState({ founders: [], cap: 50, taken: 0, remaining: 50 });
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    api.get("/founders").then((r) => setData(r.data)).catch(() => {});
    api.get("/founders/top-referrers").then((r) => setLeaders(r.data.leaderboard || [])).catch(() => {});
  }, []);

  // Render empty slots up to 8 visible to show scarcity
  const VISIBLE = 8;
  const filled = data.founders.slice(0, VISIBLE);
  const emptyCount = Math.max(0, VISIBLE - filled.length);

  return (
    <section className="container-app py-24" data-testid="hall-of-founders">
      <div className="grid lg:grid-cols-12 gap-12 items-end mb-10">
        <div className="lg:col-span-7">
          <div className="label-eyebrow inline-flex items-center gap-2 text-amber-300">
            <Crown size={14} /> Hall of Founders
          </div>
          <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">
            Los <span className="text-amber-300">{data.cap}</span> que construyen Colega Link desde el día uno.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl leading-relaxed">
            Comisión 5% bloqueada de por vida, primer aviso en todas las delegaciones, voz en las decisiones de
            producto. Solo {data.cap} cupos en toda la historia de la plataforma.
          </p>
        </div>
        <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-3">
          <div className="panel p-5 w-full max-w-sm">
            <div className="flex items-baseline justify-between gap-2">
              <span className="label-eyebrow">Cupos restantes</span>
              <span className="text-xs text-muted-foreground">{data.taken}/{data.cap} tomados</span>
            </div>
            <div className="serif text-5xl mt-2 text-amber-300" data-testid="founders-remaining-count">{data.remaining}</div>
            <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400" style={{ width: `${(data.taken / data.cap) * 100}%` }} />
            </div>
            <Link to="/planes" className="mt-5 btn-primary bg-amber-500 hover:bg-amber-400 text-amber-950 w-full" data-testid="hof-cta">
              Reservar mi cupo <ArrowUpRight size={14} className="ml-2" />
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {filled.map((f) => (
          <div key={f.id} className="panel p-4 ring-1 ring-amber-400/15 hover:ring-amber-400/40 transition-all" data-testid={`founder-card-${f.founder_number}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="w-12 h-12 rounded-full bg-amber-400/20 border-2 border-amber-400/40 flex items-center justify-center overflow-hidden">
                {f.has_avatar ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${f.avatar_url}`}
                    alt={f.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="text-amber-300 font-bold text-xs">#{f.founder_number}</span>
                )}
              </div>
              <Crown size={14} className="text-amber-400 mt-1" />
            </div>
            <div className="mt-2 text-[10px] text-amber-300 font-bold tracking-wider">FUNDADOR N°{String(f.founder_number).padStart(2, "0")}</div>
            <div className="mt-1 font-medium text-sm leading-tight">{f.name}</div>
            <div className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1">
              <MapPin size={10} />{f.comuna || f.region || "Chile"}
            </div>
            {f.reputation > 0 && (
              <div className="mt-2 text-xs inline-flex items-center gap-1">
                <Star size={10} className="text-amber-400 fill-amber-400" />
                <span>{f.reputation.toFixed(1)}</span>
              </div>
            )}
            <div className="mt-2 text-[10px] text-muted-foreground capitalize">{f.role === "lawyer" ? "Abogado/a" : "Mediador/a"}</div>
          </div>
        ))}
        {Array.from({ length: emptyCount }).map((_, i) => (
          <div key={`empty-${i}`} className="panel p-4 border-dashed opacity-50 flex flex-col items-center justify-center text-center min-h-[140px]">
            <Crown size={20} className="text-white/20" />
            <div className="mt-2 text-xs text-muted-foreground">Cupo disponible</div>
            <div className="text-[10px] text-muted-foreground">#{data.taken + i + 1}</div>
          </div>
        ))}
      </div>

      {/* Top Referrers leaderboard — only show if at least 1 founder has referrals */}
      {leaders.length > 0 && (
        <div className="mt-12 panel-glow p-8" data-testid="top-referrers-section">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="label-eyebrow inline-flex items-center gap-2 text-amber-300">
                <Trophy size={14} /> Top Invitadores del Mes
              </div>
              <h3 className="serif text-3xl mt-2">Los embajadores que más comunidad atraen</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg">Cada invitado activado les da +1 delegación gratis ese mes. Sin filtrar contacto, solo reconocimiento.</p>
            </div>
            <div className="text-xs text-muted-foreground">Últimos 30 días</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {leaders.map((l, idx) => {
              const podium = ["from-amber-400 to-amber-600", "from-slate-300 to-slate-500", "from-amber-700 to-amber-900"];
              const medals = ["🥇", "🥈", "🥉"];
              return (
                <div key={l.user_id || l.referral_code || idx} className={`panel p-5 ${idx === 0 ? "ring-2 ring-amber-400/40" : "ring-1 ring-white/5"}`} data-testid={`leader-rank-${idx + 1}`}>
                  <div className="flex items-start justify-between">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${podium[idx]} flex items-center justify-center text-base font-bold shrink-0`}>
                      {idx + 1}
                    </div>
                    <span className="text-2xl" role="img" aria-label="medal">{medals[idx]}</span>
                  </div>
                  <div className="mt-4 serif text-2xl">{l.display_name}</div>
                  <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                    <Crown size={10} className="text-amber-400" /> Fundador N°{l.founder_number}
                    {l.region && <> · <MapPin size={10}/>{l.region}</>}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Gift size={14} className="text-amber-400" />
                    <span className="serif text-3xl text-amber-300">{l.referrals_count}</span>
                    <span className="text-xs text-muted-foreground">invitado{l.referrals_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
